import React from 'react';
import { Globe, Target, DollarSign, Users, BarChart3, ExternalLink, Lightbulb } from 'lucide-react';

const SECTION_CONFIG = {
    'what it offers': { icon: Lightbulb, color: '#10b981', bg: '#ecfdf5', label: 'What It Offers' },
    'target audience': { icon: Target, color: '#8b5cf6', bg: '#f5f3ff', label: 'Target Audience' },
    'pricing': { icon: DollarSign, color: '#f59e0b', bg: '#fffbeb', label: 'Pricing' },
    'similar websites': { icon: Globe, color: '#3b82f6', bg: '#eff6ff', label: 'Similar Websites' },
    'market position': { icon: BarChart3, color: '#ec4899', bg: '#fdf2f8', label: 'Market Position' },
};

const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s,)]+)|(\b[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s,)]*)?)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        const raw = match[0];
        const href = raw.startsWith('http') ? raw : `https://${raw}`;
        parts.push(
            <a key={match.index} href={href} target="_blank" rel="noopener noreferrer"
               onClick={(e) => e.stopPropagation()}
               className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors font-medium">
                {raw}
            </a>
        );
        lastIndex = match.index + raw.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 1 ? parts : text;
};

const parseSummary = (summary) => {
    if (!summary) return { overview: '', sections: [] };
    const lines = summary.split('\n').filter(l => l.trim());
    const sections = [];
    let overview = [];
    let currentSection = null;

    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.toLowerCase().startsWith('why this matters')) {
            const content = trimmed.includes(':') ? trimmed.split(':').slice(1).join(':').trim() : '';
            currentSection = { key: 'why_this_matters', content: content ? [content] : [] };
            sections.push(currentSection);
            continue;
        }

        // Check if this line starts a known section
        const lowerLine = trimmed.toLowerCase();
        let matched = false;
        for (const sectionKey of Object.keys(SECTION_CONFIG)) {
            if (lowerLine.startsWith(sectionKey + ':') || lowerLine.startsWith(sectionKey)) {
                const content = trimmed.includes(':') ? trimmed.split(':').slice(1).join(':').trim() : '';
                currentSection = { key: sectionKey, content: content ? [content] : [] };
                sections.push(currentSection);
                matched = true;
                break;
            }
        }
        if (matched) continue;

        // Add to current section or overview
        if (currentSection) {
            currentSection.content.push(trimmed);
        } else {
            overview.push(trimmed);
        }
    }

    return { overview: overview.join(' '), sections };
};

const WebsiteAnalysisView = ({ summary, url }) => {
    const { overview, sections } = parseSummary(summary);
    const domain = (() => { try { return new URL(url).hostname.replace('www.', ''); } catch { return url; } })();

    return (
        <div className="space-y-5" data-testid="website-analysis-view">
            {/* Header badge */}
            <div className="flex items-center gap-2 mb-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-xs font-semibold tracking-wide uppercase">
                    <Globe className="w-3 h-3" />
                    Website Analysis
                </div>
            </div>

            {/* Overview */}
            {overview && (
                <p className="text-foreground/80 leading-relaxed text-[15px]">{linkify(overview)}</p>
            )}

            {/* Section cards */}
            <div className="grid gap-4">
                {sections.map((section, idx) => {
                    if (section.key === 'why_this_matters') {
                        return (
                            <div key={idx} className="rounded-xl p-4 border-l-4 border-emerald-400 bg-emerald-50/50" data-testid="why-this-matters">
                                <p className="text-xs font-mono uppercase tracking-wider text-emerald-700 mb-1.5 font-semibold">Why This Matters</p>
                                <p className="text-foreground/80 leading-relaxed text-sm">{linkify(section.content.join(' '))}</p>
                            </div>
                        );
                    }

                    const config = SECTION_CONFIG[section.key];
                    if (!config) return null;
                    const Icon = config.icon;

                    // Parse bullets vs paragraphs
                    const bullets = [];
                    const paragraphs = [];
                    for (const line of section.content) {
                        if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
                            bullets.push(line.replace(/^[•\-*]\s*/, ''));
                        } else {
                            paragraphs.push(line);
                        }
                    }

                    return (
                        <div key={idx} className="rounded-xl border border-border/60 overflow-hidden" data-testid={`section-${section.key.replace(/\s+/g, '-')}`}>
                            <div className="flex items-center gap-2.5 px-4 py-3" style={{ backgroundColor: config.bg }}>
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
                                    <Icon className="w-4 h-4" style={{ color: config.color }} />
                                </div>
                                <span className="text-sm font-semibold tracking-tight" style={{ color: config.color }}>
                                    {config.label}
                                </span>
                            </div>
                            <div className="px-4 py-3 space-y-2 bg-white">
                                {paragraphs.length > 0 && (
                                    <p className="text-foreground/80 leading-relaxed text-sm">{linkify(paragraphs.join(' '))}</p>
                                )}
                                {bullets.length > 0 && (
                                    <ul className="space-y-2">
                                        {bullets.map((b, i) => (
                                            <li key={`bullet-${b.slice(0, 20)}`} className="flex items-start gap-2.5 text-sm text-foreground/80 leading-relaxed">
                                                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: config.color }} />
                                                <span>{linkify(b)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Visit button */}
            <div className="pt-2">
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                   data-testid="visit-website-btn">
                    <ExternalLink className="w-4 h-4" />
                    Visit {domain}
                </a>
            </div>
        </div>
    );
};

export default WebsiteAnalysisView;
